import React, { useEffect, useRef, useState } from "react";
import "../styles/DeleveryManagment.css";
import BusinessHubHeader from "../components/BusinessHubHeader.js";
import BusinessHubNavigation from "../components/BusinessHubNavigation.js";
import "../styles/UserManagmentPage.css";
import DeleveryManagementIcon from "../icons/DeleveryManagementIcon.js";
import FilterIcon from "../icons/BusinessHub/FilterIcon.js";
import PlusIcon from "../icons/BusinessHub/PlusIcon.js";
import DateRangePicker from "./DateRangePicker.js";
import DateIcon from "../icons/BusinessHub/DateIcon.js";
import WaitingClockIcon from "../icons/WaitingClockIcon.js";
import CustomDropdown from "./StatusDropdown.js";
import SearchIcon from "../icons/BusinessHub/SearchIcon.js";
import ArrowBackIcon from "../icons/BusinessHub/ArrowBackIcon.js";
import ArrowFront from "../icons/BusinessHub/ArrowFront.js";
import ArrowDownIcon from "../icons/BusinessHub/ArrowDownIcon.js";
import BuildingIcon from "../icons/BuildingIcon.js";
import IndividualIcon from "../icons/IndividualIcon.js";
import VerifiedSheild from "../icons/VerifiedSheild.js";
import PicIcon from "../icons/PicIcon.js";
import ShippingBusIcon from "../icons/ShippingBusIcon.js";
import VendorOrderController from "../controllers/VendorOrderController.js";
import { userService } from "../services/userService.js";

const normalizeDate = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const addDays = (date, days) => {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + days);
  return normalizeDate(nextDate);
};

const formatDateRangeValue = (date) => {
  if (!date || Number.isNaN(date.getTime())) return "";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const getFirstValue = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

const formatTextStatus = (value) =>
  String(value || "")
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const formatApiCurrency = (amount) => `$${Number(amount || 0).toFixed(2)}`;

const getInitials = (name, fallback) => {
  const parts = String(name || fallback || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  return (
    parts
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join("") || "OR"
  );
};

const normalizeOrderStatus = (orderStatus) => {
  const status = String(orderStatus || "").toUpperCase();

  if (
    ["PENDING", "EMPTY", "DRAFT", "PENDING_PAYMENT", "VENDOR_PENDING"].includes(
      status,
    )
  ) {
    return "pending";
  }

  if (
    [
      "ACTIVE",
      "PAYMENT_CONFIRMED",
      "VENDOR_ACCEPTED",
      "PREPARATION_IN_PROGRESS",
      "READY_FOR_SHIPMENT",
      "PICKED_UP_BY_CARRIER",
    ].includes(status)
  ) {
    return "confirmed";
  }

  if (["IN_TRANSIT", "OUT_FOR_DELIVERY"].includes(status)) {
    return "out_for_delivery";
  }

  if (["DELIVERED", "DELIVERY_CONFIRMED"].includes(status)) {
    return "delivered";
  }

  if (["VENDOR_REJECTED", "CANCELLED", "PAYMENT_FAILED"].includes(status)) {
    return "rejected";
  }

  return "pending";
};

const normalizeOrderProgress = (orderStatus) => {
  const status = String(orderStatus || "").toUpperCase();

  if (["VENDOR_REJECTED", "CANCELLED", "PAYMENT_FAILED"].includes(status)) {
    return "rejected";
  }

  if (
    [
      "VENDOR_ACCEPTED",
      "PREPARATION_IN_PROGRESS",
      "READY_FOR_SHIPMENT",
      "PICKED_UP_BY_CARRIER",
      "IN_TRANSIT",
      "OUT_FOR_DELIVERY",
      "DELIVERED",
      "DELIVERY_CONFIRMED",
    ].includes(status)
  ) {
    return status.toLowerCase();
  }

  return "vendor_pending";
};

const getOrderTimestamp = (order) => {
  const timestamp = getFirstValue(
    order?.createdAt,
    order?.createdDate,
    order?.orderDate,
    order?.updatedAt,
    order?.paymentDate,
  );

  if (!timestamp) {
    return order?.paymentStatus
      ? `Payment: ${formatTextStatus(order.paymentStatus)}`
      : "";
  }

  const date = new Date(timestamp);

  if (Number.isNaN(date.getTime())) {
    return String(timestamp);
  }

  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const getOrderAddress = (order) =>
  getFirstValue(
    order?.deliveryAddress,
    order?.shippingAddress,
    order?.address,
    order?.customerAddress,
    order?.location,
    order?.deliveryLocation,
    order?.delivery?.address,
    order?.customer?.address,
    "Delivery address not provided",
  );

const normalizeOrderItem = (item = {}) => {
  const unitPrice = Number(getFirstValue(item.unitPrice, item.price, 0));
  const quantity = Number(getFirstValue(item.quantity, 1));
  const lineTotal = Number(getFirstValue(item.lineTotal, unitPrice * quantity));

  return {
    ...item,
    product: getFirstValue(
      item.displayName,
      item.productName,
      item.name,
      "Product",
    ),
    sku: getFirstValue(item.skuId, item.sku, item.productId, "N/A"),
    quantity,
    unitPrice,
    lineTotal,
    itemPrice: formatApiCurrency(lineTotal),
    itemPriceEach: `${formatApiCurrency(unitPrice)} each`,
  };
};

const getOrdersFromResponse = (response) => {
  const data = response?.data ?? response;

  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.content)) return data.content;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.orders)) return data.orders;
  if (Array.isArray(data?.data)) return data.data;

  return [];
};

const toApiDate = (value) => {
  if (!value) return undefined;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;

  const match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  return match ? `${match[3]}-${match[2]}-${match[1]}` : value;
};

const getDashboardView = (tabKey) => {
  if (tabKey === "orders") return "ORDER_HISTORY";
  if (tabKey === "returns") return "ORDER_HISTORY";
  if (tabKey === "invoices") return "ORDER_HISTORY";
  return "ORDER_HISTORY";
};

const getStatusLabel = (value) =>
  String(value || "")
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
    .join(" ");

const normalizeOrder = (order = {}) => {
  const items = Array.isArray(order.items)
    ? order.items.map(normalizeOrderItem)
    : [];
  const fallbackFirstItem =
    items[0] ||
    normalizeOrderItem({
      productName: order.primaryProductName,
      skuId: order.primarySkuCode,
      quantity: getFirstValue(order.primaryQuantity, 1),
      unitPrice: getFirstValue(order.primaryUnitPrice, order.amount, 0),
      lineTotal:
        Number(getFirstValue(order.primaryQuantity, 1)) *
        Number(getFirstValue(order.primaryUnitPrice, order.amount, 0)),
    });
  const firstItem = fallbackFirstItem;
  const resolvedStatus = getFirstValue(order.orderStatus, order.status);
  const id = getFirstValue(order.orderNumber, order.orderId, order.id, "ORDER");
  const customer = getFirstValue(
    order.customerName,
    order.customerFullName,
    order.userName,
    order.customer?.fullName,
    order.customer?.name,
    "Customer",
  );
  const itemsTotal = items.reduce((total, item) => total + item.lineTotal, 0);
  const subtotalAmount = Number(
    getFirstValue(order.totalAmount, order.subtotalAmount, itemsTotal),
  );

  return {
    ...order,
    id: String(id).startsWith("#") ? String(id) : `#${id}`,
    rawOrderId: order.orderId,
    customer,
    time: getOrderTimestamp(order),
    product:
      items.length > 1
        ? `${firstItem.product} + ${items.length - 1} more`
        : firstItem.product,
    sku: firstItem.sku,
    quantity:
      items.reduce((total, item) => total + item.quantity, 0) ||
      Number(getFirstValue(order.primaryQuantity, 1)),
    itemPrice: formatApiCurrency(subtotalAmount),
    itemPriceEach: firstItem.itemPriceEach,
    address: getOrderAddress(order),
    initials: getInitials(customer, id),
    totalAmount: Number(
      getFirstValue(order.totalAmount, order.subtotalAmount, itemsTotal),
    ),
    taxAmount: Number(getFirstValue(order.taxAmount, 0)),
    taxPercentage: Number(getFirstValue(order.taxPercentage, 0)),
    status: normalizeOrderStatus(resolvedStatus),
    progress: normalizeOrderProgress(resolvedStatus),
    driver: getFirstValue(
      order.driverName,
      order.deliveryDriverName,
      order.driver?.fullName,
      order.driver?.name,
      "Unassigned",
    ),
    items: items.length ? items : [firstItem],
    subtotalAmount,
  };
};

const getPresetRange = (presetId) => {
  const today = normalizeDate(new Date());

  if (presetId === "today") {
    return {
      from: formatDateRangeValue(today),
      to: formatDateRangeValue(today),
    };
  }

  if (presetId === "yesterday") {
    const yesterday = addDays(today, -1);
    return {
      from: formatDateRangeValue(yesterday),
      to: formatDateRangeValue(yesterday),
    };
  }

  if (presetId === "last-7-days") {
    return {
      from: formatDateRangeValue(addDays(today, -6)),
      to: formatDateRangeValue(today),
    };
  }

  if (presetId === "last-30-days") {
    return {
      from: formatDateRangeValue(addDays(today, -29)),
      to: formatDateRangeValue(today),
    };
  }

  if (presetId === "this-month") {
    return {
      from: formatDateRangeValue(
        new Date(today.getFullYear(), today.getMonth(), 1),
      ),
      to: formatDateRangeValue(today),
    };
  }

  if (presetId === "last-month") {
    return {
      from: formatDateRangeValue(
        new Date(today.getFullYear(), today.getMonth() - 1, 1),
      ),
      to: formatDateRangeValue(
        new Date(today.getFullYear(), today.getMonth(), 0),
      ),
    };
  }

  if (presetId === "this-year") {
    return {
      from: formatDateRangeValue(new Date(today.getFullYear(), 0, 1)),
      to: formatDateRangeValue(today),
    };
  }

  return { from: "", to: "" };
};

const deliveryDatePresets = [
  { label: "Today", value: "today" },
  { label: "Yesterday", value: "yesterday" },
  { label: "Last 7 days", value: "last-7-days" },
  { label: "Last 30 Days", value: "last-30-days" },
  { label: "This Month", value: "this-month" },
  { label: "Last Month", value: "last-month" },
  { label: "This Year", value: "this-year" },
  { label: "Custom Range", value: "custom-range" },
];

const deliveryStatusOptions = [
  { label: "All Statuses", value: "all" },
  { label: "Picked Up", value: "picked-up" },
  { label: "In Transit", value: "in-transit" },
  { label: "Delivered", value: "delivered" },
  { label: "Delayed", value: "delayed" },
  { label: "Returned", value: "returned" },
  { label: "Canceled", value: "canceled" },
  { label: "Delivered & Returned", value: "delivered-returned" },
  { label: "New", value: "new" },
];

const deliveryTypeOptions = [
  { label: "Transfer", value: "transfer" },
  { label: "Delivery", value: "delivery" },
  { label: "Pickup", value: "pickup" },
];

const deliveryDriverOptions = [
  { label: "Ahmad Karimi", value: "ahmad-karimi" },
  { label: "Nadine Haddad", value: "nadine-haddad" },
  { label: "Omar Darwish", value: "omar-darwish" },
  { label: "Maya Nasser", value: "maya-nasser" },
];

const returnReasonOptions = [
  { label: "Customer Rejected Order", value: "customer-rejected-order" },
  { label: "Customer Unavailable", value: "customer-unavailable" },
  { label: "Incorrect Address", value: "incorrect-address" },
  { label: "Wrong Product", value: "wrong-product" },
  { label: "Payment Refund", value: "payment-refund" },
  {
    label: "Customer Requested Cancellation",
    value: "customer-requested-cancellation",
  },
  { label: "Delivery Delay", value: "delivery-delay" },
  { label: "Other", value: "other" },
];

const transferCompanyOptions = [
  {
    id: "company-dhl",
    initials: "DH",
    name: "DHL",
    description: "On Time - Trusted - Efficient",
    rating: "4.6",
    reviews: "190",
    eta: "3 - 5 hrs",
  },
  {
    id: "company-aramex",
    initials: "AR",
    name: "Aramex",
    description: "We ship it, we deliver it",
    rating: "4.5",
    reviews: "150",
    eta: "4 - 6 hrs",
  },
];

const transferDriverOptions = [
  {
    id: "driver-omar-al-hasan",
    name: "Omar Al Hasan",
    description: "Deliveries:320",
    rating: "4.8",
    reviews: "126",
    vehicle: "Honda PCX 150",
    plate: "B 123456",
    eta: "15 - 20 mins",
    distance: "2.3 km away",
  },
  {
    id: "driver-hadi-khaled",
    name: "Hadi Khaled",
    description: "Deliveries:245",
    rating: "4.8",
    reviews: "98",
    vehicle: "Yamaha NMAX 155",
    plate: "B 654789",
    eta: "18 - 22 mins",
    distance: "2.7 km away",
  },
  {
    id: "driver-mohamad-ali",
    name: "Mohamad Ali",
    description: "Deliveries:280",
    rating: "4.7",
    reviews: "48",
    vehicle: "Honda Dio 14",
    plate: "B 125489",
    eta: "22 - 28 mins",
    distance: "2.1 km away",
  },
];

const returnOrders = [
  {
    id: "RET-0041",
    orderId: "ORD-2456",
    customer: "Giulia Ferretti",
    reason: "Wrong Product",
    date: "July 29,2026",
    driver: "Omar Bitar",
    status: "Completed",
    statusValue: "completed",
    refund: "$199.00",
  },
  {
    id: "RET-0042",
    orderId: "ORD-2458",
    customer: "Amir Hosseini",
    reason: "Customer Requested Cancellation",
    date: "July 28,2026",
    driver: "Wassim Bitar",
    status: "Completed",
    statusValue: "completed",
    refund: "$89.00",
  },
  {
    id: "RET-0040",
    orderId: "ORD-2447",
    customer: "Sara Khalil",
    reason: "Customer Unavailable",
    date: "July 27,2026",
    driver: "Khalil Mansour",
    status: "Pending",
    statusValue: "pending",
    refund: "$55.00",
  },
];

const deliveryInvoices = [
  {
    id: "INV-1081",
    orderId: "ORD-2451",
    company: "DHL",
    customer: "Lena Hartmann",
    deliveryFee: "$12.00",
    codAmount: "-",
    insurance: "$2.50",
    total: "$14.50",
    status: "Active",
    statusValue: "active",
    date: "July 30, 2026",
  },
  {
    id: "INV-1062",
    orderId: "ORD-2452",
    company: "Aramex",
    customer: "Marcus Silva",
    deliveryFee: "$15.00",
    codAmount: "$244.50",
    insurance: "$3.00",
    total: "$262.50",
    status: "Active",
    statusValue: "active",
    date: "July 30, 2026",
  },
  {
    id: "INV-1079",
    orderId: "ORD-2453",
    company: "Azalea",
    customer: "Nour Al Rashid",
    deliveryFee: "$8.00",
    codAmount: "-",
    insurance: "-",
    total: "$8.00",
    status: "Completed",
    statusValue: "completed",
    date: "July 29, 2026",
  },
  {
    id: "INV-1078",
    orderId: "ORD-2456",
    company: "UPS",
    customer: "Giulia Ferretti",
    deliveryFee: "$18.00",
    codAmount: "-",
    insurance: "$4.00",
    total: "$22.00",
    status: "Canceled",
    statusValue: "canceled",
    date: "July 28, 2026",
  },
];

const newDeliveryMethodOptions = [
  { label: "Azalea", value: "azalea" },
  { label: "Third-Party", value: "third-party" },
];

const newDeliveryCompanyOptions = [
  { label: "DHL", value: "dhl" },
  { label: "Aramex", value: "aramex" },
  { label: "FedEx", value: "fedex" },
  { label: "UPS", value: "ups" },
];

const deliveryRowsPerPage = 8;

const getStatusClassName = (value) => value.replace(/\s+/g, "-").toLowerCase();

const getDeliveryStatusFromOrderStatus = (status) => {
  if (status === "confirmed") return { label: "Picked Up", value: "picked-up" };
  if (status === "out_for_delivery") {
    return { label: "In Transit", value: "in-transit" };
  }
  if (status === "delivered") return { label: "Delivered", value: "delivered" };
  if (status === "rejected") return { label: "Canceled", value: "canceled" };

  return { label: "New", value: "new" };
};

const mapApiOrderToDeliveryRow = (order) => {
  const deliveryStatus = getDeliveryStatusFromOrderStatus(order.status);
  const rawId = String(order.id || "");

  return {
    ...order,
    id: rawId.startsWith("#") ? rawId.slice(1) : rawId,
    rawOrderId: order.rawOrderId,
    customer: order.customer,
    location: order.address,
    deliveredBy: order.driver === "Unassigned" ? "Third Party" : "Azalea",
    products: order.product,
    packages: order.quantity || 1,
    deliveryStatus: deliveryStatus.label,
    statusValue: deliveryStatus.value,
    payment: order.paymentStatus
      ? formatTextStatus(order.paymentStatus)
      : order.status === "rejected"
        ? "Refunded"
        : "Paid",
    amount: formatApiCurrency(order.totalAmount),
    dimensions: getFirstValue(order.dimensions, order.packageDimensions, "-"),
    apiOrder: order,
  };
};

function DeleveryManagment() {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState("csv");
  const [exportScope, setExportScope] = useState("all");
  const [isNewDeliveryOpen, setIsNewDeliveryOpen] = useState(false);
  const [newDeliveryMethod, setNewDeliveryMethod] = useState("third-party");
  const [newDeliveryCompany, setNewDeliveryCompany] = useState("dhl");
  const [isTransferPopupOpen, setIsTransferPopupOpen] = useState(false);
  const [transferTab, setTransferTab] = useState("companies");
  const [transferSearch, setTransferSearch] = useState("");
  const [selectedTransferOption, setSelectedTransferOption] =
    useState("company-dhl");
  const [activeDeliveryTab, setActiveDeliveryTab] = useState("orders");
  const [isReturnSubmitOpen, setIsReturnSubmitOpen] = useState(false);
  const [returnReason, setReturnReason] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [deliveryTypeFilter, setDeliveryTypeFilter] = useState("transfer");
  const [selectedDeliveryIds, setSelectedDeliveryIds] = useState([]);
  const [selectedReturnIds, setSelectedReturnIds] = useState([]);
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState([]);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [assignDriverOrder, setAssignDriverOrder] = useState(null);
  const [assignDriverValue, setAssignDriverValue] = useState("");
  const [invoiceOrder, setInvoiceOrder] = useState(null);
  const [deliveryApiOrders, setDeliveryApiOrders] = useState([]);
  const [hasLoadedDeliveryApiDashboard, setHasLoadedDeliveryApiDashboard] =
    useState(false);
  const [deliveryApiStats, setDeliveryApiStats] = useState({
    totalOrders: 0,
    pendingCount: 0,
    confirmedCount: 0,
    outForDeliveryCount: 0,
  });
  const [deliveryApiPagination, setDeliveryApiPagination] = useState({
    total: 0,
    page: 0,
    size: deliveryRowsPerPage,
    totalPages: 0,
  });
  const [isLoadingDeliveryOrders, setIsLoadingDeliveryOrders] =
    useState(false);
  const [deliveryApiError, setDeliveryApiError] = useState("");
  const [selectedDeliveryOrderDetails, setSelectedDeliveryOrderDetails] =
    useState(null);
  const [selectedDeliveryNextStatus, setSelectedDeliveryNextStatus] =
    useState("");
  const [isSubmittingDeliveryAction, setIsSubmittingDeliveryAction] =
    useState(false);
  const [deliveryActionError, setDeliveryActionError] = useState("");
  const [appliedPreset, setAppliedPreset] = useState("last-30-days");
  const [draftPreset, setDraftPreset] = useState("last-30-days");
  const [appliedRange, setAppliedRange] = useState(
    getPresetRange("last-30-days"),
  );
  const [draftRange, setDraftRange] = useState(getPresetRange("last-30-days"));
  const [rangePickerCloseSignal, setRangePickerCloseSignal] = useState(0);
  const filterDialogRef = useRef(null);

  useEffect(() => {
    if (
      !isFilterOpen &&
      !isExportOpen &&
      !isNewDeliveryOpen &&
      !isTransferPopupOpen &&
      !isReturnSubmitOpen &&
      !assignDriverOrder &&
      !invoiceOrder
    ) {
      return undefined;
    }

    const onEsc = (event) => {
      if (event.key !== "Escape") return;

      if (isNewDeliveryOpen) {
        setIsNewDeliveryOpen(false);
        return;
      }

      if (isTransferPopupOpen) {
        setIsTransferPopupOpen(false);
        return;
      }

      if (isReturnSubmitOpen) {
        setIsReturnSubmitOpen(false);
        return;
      }

      if (invoiceOrder) {
        setInvoiceOrder(null);
        return;
      }

      if (assignDriverOrder) {
        setAssignDriverOrder(null);
        return;
      }

      if (isExportOpen) {
        setIsExportOpen(false);
        return;
      }

      setIsFilterOpen(false);
    };

    document.addEventListener("keydown", onEsc);

    return () => {
      document.removeEventListener("keydown", onEsc);
    };
  }, [
    isFilterOpen,
    isExportOpen,
    isNewDeliveryOpen,
    isTransferPopupOpen,
    isReturnSubmitOpen,
    assignDriverOrder,
    invoiceOrder,
  ]);

  useEffect(() => {
    setExpandedOrderId(null);
  }, [searchValue, statusFilter, deliveryTypeFilter]);

  const openFilter = () => {
    setIsExportOpen(false);
    setIsNewDeliveryOpen(false);
    setIsTransferPopupOpen(false);
    setDraftPreset(appliedPreset);
    setDraftRange(appliedRange);
    setIsFilterOpen(true);
  };

  const closeExport = () => {
    setIsExportOpen(false);
  };

  const openExport = () => {
    setIsFilterOpen(false);
    setIsNewDeliveryOpen(false);
    setIsTransferPopupOpen(false);
    setIsReturnSubmitOpen(false);
    setAssignDriverOrder(null);
    setInvoiceOrder(null);
    setIsExportOpen(true);
  };

  const downloadExport = () => {
    if (exportSelectedRows()) {
      setIsExportOpen(false);
    }
  };

  const openNewDelivery = () => {
    setIsFilterOpen(false);
    setIsExportOpen(false);
    setIsTransferPopupOpen(false);
    setAssignDriverOrder(null);
    setInvoiceOrder(null);
    setNewDeliveryMethod("third-party");
    setNewDeliveryCompany("dhl");
    setIsNewDeliveryOpen(true);
  };

  const closeNewDelivery = () => {
    setIsNewDeliveryOpen(false);
  };

  const createDelivery = () => {
    setIsNewDeliveryOpen(false);
  };

  const openTransferPopup = () => {
    setIsFilterOpen(false);
    setIsExportOpen(false);
    setIsNewDeliveryOpen(false);
    setIsReturnSubmitOpen(false);
    setAssignDriverOrder(null);
    setInvoiceOrder(null);
    setTransferSearch("");
    setIsTransferPopupOpen(true);
  };

  const closeTransferPopup = () => {
    setIsTransferPopupOpen(false);
  };

  const selectTransferTab = (tab) => {
    setTransferTab(tab);
    setSelectedTransferOption(
      tab === "companies" ? "company-dhl" : "driver-omar-al-hasan",
    );
  };

  const confirmTransferSelection = () => {
    setDeliveryTypeFilter("transfer");
    setIsTransferPopupOpen(false);
  };

  const selectDeliveryTab = (tab) => {
    setActiveDeliveryTab(tab);
    setExpandedOrderId(null);
  };

  const loadDeliveryOrders = async ({
    nextTab = activeDeliveryTab,
    nextPage = deliveryApiPagination.page,
    nextSelectedOrderId,
    nextDateRange = appliedRange,
  } = {}) => {
    setIsLoadingDeliveryOrders(true);
    setDeliveryApiError("");

    try {
      const response = await VendorOrderController.getOrdersDashboard({
        Authorization: userService.getUser(),
        companyId: userService.getCompID(),
        view: getDashboardView(nextTab),
        selectedOrderId: nextSelectedOrderId,
        fromDate: toApiDate(nextDateRange.from),
        toDate: toApiDate(nextDateRange.to),
        page: nextPage,
        size: deliveryApiPagination.size,
      });
      const data = response?.data ?? response ?? {};
      const normalizedOrders = getOrdersFromResponse(data).map(normalizeOrder);

      setHasLoadedDeliveryApiDashboard(true);
      setDeliveryApiOrders(normalizedOrders);
      setDeliveryApiStats({
        totalOrders: Number(data?.stats?.orderHistory || 0),
        pendingCount: Number(data?.stats?.pending || 0),
        confirmedCount: Number(data?.stats?.confirmed || 0),
        outForDeliveryCount: Number(data?.stats?.outForDelivery || 0),
      });
      setDeliveryApiPagination({
        total: Number(data?.total || 0),
        page: Number(data?.page || nextPage || 0),
        size: Number(data?.size || deliveryApiPagination.size),
        totalPages: Number(data?.totalPages || 0),
      });
      const selectedDetails = data?.selectedOrder ?? null;
      setSelectedDeliveryOrderDetails(selectedDetails);
      setSelectedDeliveryNextStatus(
        Array.isArray(selectedDetails?.availableNextStatuses) &&
          selectedDetails.availableNextStatuses.length > 0
          ? selectedDetails.availableNextStatuses[0]
          : "",
      );
      console.log("Delivery Management API orders loaded", {
        total: Number(data?.total || normalizedOrders.length || 0),
        page: Number(data?.page || nextPage || 0),
        orders: normalizedOrders,
        selectedOrder: data?.selectedOrder ?? null,
      });
    } catch (error) {
      console.error("Failed to load delivery management orders", error);
      setHasLoadedDeliveryApiDashboard(false);
      setDeliveryApiOrders([]);
      setDeliveryApiStats({
        totalOrders: 0,
        pendingCount: 0,
        confirmedCount: 0,
        outForDeliveryCount: 0,
      });
      setSelectedDeliveryOrderDetails(null);
      setSelectedDeliveryNextStatus("");
      setDeliveryApiError("Failed to load delivery orders.");
      setDeliveryApiPagination((current) => ({
        ...current,
        total: 0,
        totalPages: 0,
      }));
    } finally {
      setIsLoadingDeliveryOrders(false);
    }
  };

  useEffect(() => {
    if (activeDeliveryTab !== "orders") {
      return;
    }

    setExpandedOrderId(null);
    setSelectedDeliveryOrderDetails(null);
    setSelectedDeliveryNextStatus("");
    setDeliveryActionError("");
    loadDeliveryOrders({
      nextTab: "orders",
      nextPage: 0,
      nextSelectedOrderId: null,
      nextDateRange: appliedRange,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDeliveryTab, appliedRange.from, appliedRange.to]);

  const openReturnSubmit = () => {
    setIsFilterOpen(false);
    setIsExportOpen(false);
    setIsNewDeliveryOpen(false);
    setIsTransferPopupOpen(false);
    setAssignDriverOrder(null);
    setInvoiceOrder(null);
    setReturnReason("");
    setIsReturnSubmitOpen(true);
  };

  const closeReturnSubmit = () => {
    setIsReturnSubmitOpen(false);
  };

  const submitReturnReport = () => {
    setIsReturnSubmitOpen(false);
  };

  const selectPreset = (presetId) => {
    setDraftPreset(presetId);

    if (presetId !== "custom-range") {
      setDraftRange(getPresetRange(presetId));
    }
  };

  const updateDraftRange = (range) => {
    setDraftPreset("custom-range");
    setDraftRange(range);
  };

  const applyFilter = () => {
    setAppliedPreset(draftPreset);
    setAppliedRange(draftRange);
    setIsFilterOpen(false);
  };

  const cancelFilter = () => {
    setDraftPreset(appliedPreset);
    setDraftRange(appliedRange);
    setIsFilterOpen(false);
  };

  const closeRangePopoverIfClickOutside = (event) => {
    const rangePopover = document.querySelector(
      ".drp-popover.delivery-filter-range-popover",
    );

    if (!rangePopover || rangePopover.contains(event.target)) {
      return false;
    }

    setRangePickerCloseSignal((prev) => prev + 1);
    return true;
  };

  const handleFilterOverlayMouseDown = (event) => {
    const rangePopover = document.querySelector(
      ".drp-popover.delivery-filter-range-popover",
    );

    if (rangePopover && !rangePopover.contains(event.target)) {
      return;
    }

    cancelFilter();
  };

  const deliveryOrderRows = deliveryApiOrders.map(mapApiOrderToDeliveryRow);
  const hasApiDeliveryRows =
    hasLoadedDeliveryApiDashboard && deliveryApiOrders.length > 0;

  const filteredDeliveryOrders = deliveryOrderRows.filter((order) => {
    const search = searchValue.trim().toLowerCase();
    const matchesSearch =
      !search ||
      order.id.toLowerCase().includes(search) ||
      order.customer.toLowerCase().includes(search) ||
      order.products.toLowerCase().includes(search);
    const matchesStatus =
      statusFilter === "all" || order.statusValue === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const displayedDeliveryStats = deliveryApiStats;

  const transferSearchTerm = transferSearch.trim().toLowerCase();
  const activeTransferOptions =
    transferTab === "companies"
      ? transferCompanyOptions
      : transferDriverOptions;
  const filteredTransferOptions = activeTransferOptions.filter((option) => {
    if (!transferSearchTerm) return true;

    return (
      option.name.toLowerCase().includes(transferSearchTerm) ||
      option.description.toLowerCase().includes(transferSearchTerm)
    );
  });

  const totalDeliveryPages = Math.max(
    1,
    deliveryApiPagination.totalPages ||
      Math.ceil(filteredDeliveryOrders.length / deliveryRowsPerPage),
  );
  const displayDeliveryPage = deliveryApiPagination.page + 1;
  const currentDeliveryOrders = hasLoadedDeliveryApiDashboard
    ? filteredDeliveryOrders
    : [];
  const isDeliveryTableLoading =
    (isLoadingDeliveryOrders || !hasLoadedDeliveryApiDashboard) &&
    !deliveryApiError &&
    !expandedOrderId;
  const shouldShowDeliveryTableError =
    Boolean(deliveryApiError) && !isDeliveryTableLoading && !expandedOrderId;
  const shouldShowDeliveryEmptyState =
    !isDeliveryTableLoading &&
    !shouldShowDeliveryTableError &&
    currentDeliveryOrders.length === 0;
  const allDeliveryRowsChecked =
    currentDeliveryOrders.length > 0 &&
    currentDeliveryOrders.every((order) =>
      selectedDeliveryIds.includes(order.id),
    );

  const toggleAllDeliveries = (event) => {
    if (event.target.checked) {
      setSelectedDeliveryIds((prev) => [
        ...new Set([
          ...prev,
          ...currentDeliveryOrders.map((order) => order.id),
        ]),
      ]);
      return;
    }

    setSelectedDeliveryIds((prev) =>
      prev.filter(
        (orderId) =>
          !currentDeliveryOrders.some((order) => order.id === orderId),
      ),
    );
  };

  const toggleDeliveryRow = (orderId) => {
    setSelectedDeliveryIds((prev) =>
      prev.includes(orderId)
        ? prev.filter((selectedId) => selectedId !== orderId)
        : [...prev, orderId],
    );
  };

  const toggleDeliveryDetails = (order) => {
    const orderId = order?.id;

    if (!orderId) return;

    if (expandedOrderId === orderId) {
      setExpandedOrderId(null);
      setSelectedDeliveryOrderDetails(null);
      setDeliveryApiError("");
      setSelectedDeliveryNextStatus("");
      setDeliveryActionError("");
      return;
    }

    setExpandedOrderId(orderId);
    setDeliveryActionError("");

    if (hasApiDeliveryRows) {
      setSelectedDeliveryOrderDetails(null);
      setSelectedDeliveryNextStatus("");
      loadDeliveryOrders({
        nextSelectedOrderId: order.rawOrderId || order.apiOrder?.rawOrderId,
      });
    }
  };

  const getExpandedApiDetails = (order) => {
    if (!hasApiDeliveryRows || !selectedDeliveryOrderDetails) return null;

    const detailsOrderId = String(
      getFirstValue(
        selectedDeliveryOrderDetails.orderId,
        selectedDeliveryOrderDetails.id,
        "",
      ),
    );
    const rowOrderId = String(order.rawOrderId || order.apiOrder?.rawOrderId || "");

    if (detailsOrderId && rowOrderId && detailsOrderId !== rowOrderId) {
      return null;
    }

    return selectedDeliveryOrderDetails;
  };

  const getExpandedProducts = (details, order) => {
    if (!details) return order.products;

    const items = Array.isArray(details.items) ? details.items : [];

    if (items.length === 0) return order.products;

    return items
      .map((item) =>
        getFirstValue(item.displayName, item.productName, item.name, "Product"),
      )
      .join(", ");
  };

  const getExpandedPackages = (details, order) => {
    if (!details || !Array.isArray(details.items)) return order.packages;

    return (
      details.items.reduce(
        (total, item) => total + Number(getFirstValue(item.quantity, 1)),
        0,
      ) || order.packages
    );
  };

  const getAvailableNextStatuses = (details) =>
    Array.isArray(details?.availableNextStatuses)
      ? details.availableNextStatuses
      : [];

  const canRespondToPendingDeliveryOrder = (details, order) => {
    if (!hasApiDeliveryRows) return false;

    const rawStatus = String(
      getFirstValue(
        details?.status,
        order?.apiOrder?.orderStatus,
        order?.apiOrder?.status,
        order?.status,
      ),
    ).toUpperCase();

    return rawStatus === "VENDOR_PENDING" || order?.status === "pending";
  };

  const handleDeliveryStatusUpdate = async (order) => {
    const orderId = order?.rawOrderId || order?.apiOrder?.rawOrderId;

    if (
      !hasApiDeliveryRows ||
      !orderId ||
      !selectedDeliveryNextStatus ||
      isSubmittingDeliveryAction
    ) {
      return;
    }

    setIsSubmittingDeliveryAction(true);
    setDeliveryActionError("");

    try {
      await VendorOrderController.updateStatus({
        Authorization: userService.getUser(),
        request: {
          orderId,
          status: selectedDeliveryNextStatus,
        },
      });

      await loadDeliveryOrders({
        nextPage: deliveryApiPagination.page,
        nextSelectedOrderId: orderId,
      });
    } catch (error) {
      console.error("Failed to update delivery order status", error);
      setDeliveryActionError("Failed to update delivery status.");
    } finally {
      setIsSubmittingDeliveryAction(false);
    }
  };

  const handleDeliveryOrderAction = async (action, order) => {
    const orderId = order?.rawOrderId || order?.apiOrder?.rawOrderId;

    if (!hasApiDeliveryRows || !orderId || isSubmittingDeliveryAction) {
      return;
    }

    setIsSubmittingDeliveryAction(true);
    setDeliveryActionError("");

    try {
      if (action === "accept") {
        await VendorOrderController.acceptOrder({
          Authorization: userService.getUser(),
          orderId,
        });
      } else {
        await VendorOrderController.rejectOrder({
          Authorization: userService.getUser(),
          orderId,
        });
      }

      await loadDeliveryOrders({
        nextPage: deliveryApiPagination.page,
        nextSelectedOrderId: orderId,
      });
    } catch (error) {
      console.error(`Failed to ${action} delivery order`, error);
      setDeliveryActionError(
        `Failed to ${action === "accept" ? "accept" : "reject"} order.`,
      );
    } finally {
      setIsSubmittingDeliveryAction(false);
    }
  };

  const handleDeliveryPageChange = (direction) => {
    const nextPage = deliveryApiPagination.page + direction;

    if (nextPage < 0 || nextPage >= totalDeliveryPages) {
      return;
    }

    setExpandedOrderId(null);
    loadDeliveryOrders({
      nextPage,
      nextSelectedOrderId: null,
    });
  };

  const allReturnRowsChecked =
    returnOrders.length > 0 &&
    returnOrders.every((returnOrder) =>
      selectedReturnIds.includes(returnOrder.id),
    );

  const toggleAllReturns = () => {
    if (allReturnRowsChecked) {
      setSelectedReturnIds([]);
      return;
    }

    setSelectedReturnIds(returnOrders.map((returnOrder) => returnOrder.id));
  };

  const toggleReturnRow = (returnId) => {
    setSelectedReturnIds((prev) =>
      prev.includes(returnId)
        ? prev.filter((selectedId) => selectedId !== returnId)
        : [...prev, returnId],
    );
  };

  const allInvoiceRowsChecked =
    deliveryInvoices.length > 0 &&
    deliveryInvoices.every((invoice) =>
      selectedInvoiceIds.includes(invoice.id),
    );

  const toggleAllInvoices = () => {
    if (allInvoiceRowsChecked) {
      setSelectedInvoiceIds([]);
      return;
    }

    setSelectedInvoiceIds(deliveryInvoices.map((invoice) => invoice.id));
  };

  const toggleInvoiceRow = (invoiceId) => {
    setSelectedInvoiceIds((prev) =>
      prev.includes(invoiceId)
        ? prev.filter((selectedId) => selectedId !== invoiceId)
        : [...prev, invoiceId],
    );
  };

  const escapeCsvCell = (value) => {
    const text = value === null || value === undefined ? "" : String(value);
    return `"${text.replace(/"/g, '""')}"`;
  };

  const downloadCsv = (filename, columns, rows) => {
    const csv = [
      columns.map((column) => escapeCsvCell(column.label)).join(","),
      ...rows.map((row) =>
        columns.map((column) => escapeCsvCell(row[column.key])).join(","),
      ),
    ].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const downloadJson = (filename, columns, rows) => {
    const formattedRows = rows.map((row) =>
      columns.reduce((entry, column) => {
        entry[column.label] = row[column.key] ?? "";
        return entry;
      }, {}),
    );
    const blob = new Blob([JSON.stringify(formattedRows, null, 2)], {
      type: "application/json;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getActiveExportConfig = () => {
    const exportMap = {
      orders: {
        label: "orders",
        selectedIds: selectedDeliveryIds,
        allRows: deliveryOrderRows,
        filteredRows: filteredDeliveryOrders,
        columns: [
          { label: "Order #", key: "id" },
          { label: "Customer", key: "customer" },
          { label: "Location", key: "location" },
          { label: "Delivered By", key: "deliveredBy" },
          { label: "Products", key: "products" },
          { label: "Packages", key: "packages" },
          { label: "Delivery Status", key: "deliveryStatus" },
          { label: "Payment", key: "payment" },
          { label: "Amount", key: "amount" },
          { label: "Pkg Dimensions(CM)", key: "dimensions" },
        ],
      },
      returns: {
        label: "returns",
        selectedIds: selectedReturnIds,
        allRows: returnOrders,
        filteredRows: returnOrders,
        columns: [
          { label: "Return ID", key: "id" },
          { label: "Order #", key: "orderId" },
          { label: "Customer", key: "customer" },
          { label: "Return Reason", key: "reason" },
          { label: "Date", key: "date" },
          { label: "Driver", key: "driver" },
          { label: "Status", key: "status" },
          { label: "Refund", key: "refund" },
        ],
      },
      invoices: {
        label: "invoices",
        selectedIds: selectedInvoiceIds,
        allRows: deliveryInvoices,
        filteredRows: deliveryInvoices,
        columns: [
          { label: "Invoice #", key: "id" },
          { label: "Order #", key: "orderId" },
          { label: "Delivery Company", key: "company" },
          { label: "Customer", key: "customer" },
          { label: "Delivery Fee", key: "deliveryFee" },
          { label: "COD Amount", key: "codAmount" },
          { label: "Insurance", key: "insurance" },
          { label: "Total", key: "total" },
          { label: "Status", key: "status" },
          { label: "Date", key: "date" },
        ],
      },
    };

    return exportMap[activeDeliveryTab] ?? exportMap.orders;
  };

  const exportSelectedRows = () => {
    const activeExport = getActiveExportConfig();
    const scopedRows =
      exportScope === "filtered"
        ? activeExport.filteredRows
        : activeExport.allRows;
    const selectedRows = scopedRows.filter((row) =>
      activeExport.selectedIds.includes(row.id),
    );

    if (selectedRows.length === 0) {
      window.alert("Select at least one row to export.");
      return false;
    }

    const scopeSlug =
      exportScope === "filtered" ? "filtered-selected" : "selected";
    const filename = `delivery-${activeExport.label}-${scopeSlug}.${exportFormat}`;

    if (exportFormat === "json") {
      downloadJson(filename, activeExport.columns, selectedRows);
      return true;
    }

    downloadCsv(filename, activeExport.columns, selectedRows);
    return true;
  };

  const openAssignDriver = (order) => {
    setIsFilterOpen(false);
    setIsExportOpen(false);
    setInvoiceOrder(null);
    setAssignDriverValue("");
    setAssignDriverOrder(order);
  };

  const closeAssignDriver = () => {
    setAssignDriverOrder(null);
  };

  const assignDriver = () => {
    setAssignDriverOrder(null);
  };

  const openInvoice = (order) => {
    setIsFilterOpen(false);
    setIsExportOpen(false);
    setAssignDriverOrder(null);
    setInvoiceOrder(order);
  };

  const closeInvoice = () => {
    setInvoiceOrder(null);
  };

  const downloadInvoice = () => {
    setInvoiceOrder(null);
  };

  const exportPreview = getActiveExportConfig();
  const selectedRowsInAllScope = exportPreview.allRows.filter((row) =>
    exportPreview.selectedIds.includes(row.id),
  ).length;
  const selectedRowsInFilteredScope = exportPreview.filteredRows.filter((row) =>
    exportPreview.selectedIds.includes(row.id),
  ).length;

  return (
    <div className="business-hub-screen delivery-management-screen">
      {isFilterOpen ? (
        <div
          className="delivery-filter-overlay"
          onMouseDownCapture={closeRangePopoverIfClickOutside}
          onMouseDown={handleFilterOverlayMouseDown}
        >
          <div
            className="delivery-filter-dialog"
            ref={filterDialogRef}
            onMouseDownCapture={closeRangePopoverIfClickOutside}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="delivery-filter-selector">
              <label className="delivery-filter-label" htmlFor="filterPreset">
                Filter by
              </label>
              {/* <div className="delivery-filter-select-wrap">
                <CustomDropdown
                  className="delivery-filter-select"
                  openClassName="delivery-filter-select-open"
                  menuClassName="delivery-filter-select-menu"
                  optionClassName="delivery-filter-select-option"
                  value={draftPreset}
                  onChange={selectPreset}
                  options={deliveryDatePresets}
                  placeholder="Last 30 Days"
                  leftIcon={
                    <span className="filter-popup-container-rows-icon">
                      <DateIcon />
                    </span>
                  }
                  rightIcon={
                    <span className="delivery-filter-select-chevron"></span>
                  }
                />
              </div> */}
            </div>

            <div className="delivery-filter-panel">
              <div className="delivery-filter-preset-list">
                {deliveryDatePresets.map((preset) => (
                  <button
                    type="button"
                    className={`delivery-filter-preset ${
                      draftPreset === preset.value
                        ? "delivery-filter-preset-active"
                        : ""
                    }`}
                    key={preset.value}
                    onClick={() => selectPreset(preset.value)}
                  >
                    <span className="filter-popup-container-rows-icon">
                      {preset.value === "yesterday" ? (
                        <WaitingClockIcon w={[20]} h={[20]} />
                      ) : (
                        <DateIcon w={[20]} h={[20]} />
                      )}
                    </span>
                    <span>{preset.label}</span>
                  </button>
                ))}
              </div>

              <div className="delivery-filter-calendar-area">
                <div className="delivery-filter-custom-title">Custom Range</div>
                <DateRangePicker
                  compact
                  fromValue={draftRange.from}
                  toValue={draftRange.to}
                  onRangeChange={updateDraftRange}
                  className="delivery-filter-range"
                  inputsClassName="delivery-filter-range-inputs"
                  inputColClassName="delivery-filter-range-col"
                  fieldClassName="delivery-filter-range-field"
                  popoverClassName="delivery-filter-range-popover"
                  calendarCardClassName="delivery-filter-range-calendar"
                  closeSignal={rangePickerCloseSignal}
                />
              </div>
            </div>

            <div className="delivery-filter-actions">
              <button
                type="button"
                className="delivery-filter-cancel"
                onClick={cancelFilter}
              >
                Cancel
              </button>
              <button
                type="button"
                className="delivery-filter-apply"
                onClick={applyFilter}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {isExportOpen ? (
        <div className="delivery-export-overlay" onMouseDown={closeExport}>
          <div
            className="delivery-export-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="deliveryExportTitle"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="delivery-export-header">
              <h2 className="delivery-export-title" id="deliveryExportTitle">
                Export Deliveries
              </h2>
              <button
                type="button"
                className="delivery-export-close"
                aria-label="Close export deliveries"
                onClick={closeExport}
              />
            </div>

            <div className="delivery-export-section">
              <div className="delivery-export-section-title">Format</div>
              <div className="delivery-export-option-grid">
                <button
                  type="button"
                  className={`delivery-export-option ${
                    exportFormat === "csv"
                      ? "delivery-export-option-active"
                      : ""
                  }`}
                  onClick={() => setExportFormat("csv")}
                >
                  .CSV
                </button>
                <button
                  type="button"
                  className={`delivery-export-option ${
                    exportFormat === "json"
                      ? "delivery-export-option-active"
                      : ""
                  }`}
                  onClick={() => setExportFormat("json")}
                >
                  .JSON
                </button>
              </div>
            </div>

            <div className="delivery-export-section delivery-export-scope-section">
              <div className="delivery-export-section-title">Scope</div>
              <div className="delivery-export-option-grid">
                <button
                  type="button"
                  className={`delivery-export-option ${
                    exportScope === "all" ? "delivery-export-option-active" : ""
                  }`}
                  onClick={() => setExportScope("all")}
                >
                  All {exportPreview.label} ({selectedRowsInAllScope})
                </button>
                <button
                  type="button"
                  className={`delivery-export-option ${
                    exportScope === "filtered"
                      ? "delivery-export-option-active"
                      : ""
                  }`}
                  onClick={() => setExportScope("filtered")}
                >
                  Filtered view ({selectedRowsInFilteredScope})
                </button>
              </div>
            </div>

            <div className="delivery-export-actions">
              <button
                type="button"
                className="delivery-export-cancel"
                onClick={closeExport}
              >
                Cancel
              </button>
              <button
                type="button"
                className="delivery-export-download"
                onClick={downloadExport}
              >
                Download
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {assignDriverOrder ? (
        <div
          className="delivery-assign-overlay"
          onMouseDown={closeAssignDriver}
        >
          <div
            className="delivery-assign-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="deliveryAssignDriverTitle"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="delivery-assign-header">
              <h2
                className="delivery-assign-title"
                id="deliveryAssignDriverTitle"
              >
                Assign Driver
              </h2>
              <button
                type="button"
                className="delivery-assign-close"
                aria-label="Close assign driver"
                onClick={closeAssignDriver}
              />
            </div>
            <div className="delivery-assign-order-line">
              {assignDriverOrder.id} - {assignDriverOrder.customer}
            </div>
            <div className="delivery-assign-current-driver">
              Current Driver: Ahmad Karimi
            </div>
            <label className="delivery-assign-label">Driver Name</label>
            <CustomDropdown
              className="delivery-assign-driver-select"
              openClassName="delivery-assign-driver-select-open"
              menuClassName="delivery-assign-driver-select-menu"
              optionClassName="delivery-assign-driver-select-option"
              value={assignDriverValue}
              onChange={setAssignDriverValue}
              options={deliveryDriverOptions}
              placeholder="Search by name..."
              leftIcon={<SearchIcon fill="#667085" />}
              rightIcon={
                <span className="delivery-table-select-chevron"></span>
              }
              searchable
            />
            <div className="delivery-assign-actions">
              <button
                type="button"
                className="delivery-assign-cancel"
                onClick={closeAssignDriver}
              >
                Cancel
              </button>
              <button
                type="button"
                className="delivery-assign-submit"
                onClick={assignDriver}
              >
                Assign
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {isReturnSubmitOpen ? (
        <div
          className="delivery-return-submit-overlay"
          onMouseDown={closeReturnSubmit}
        >
          <div
            className="delivery-return-submit-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="deliveryReturnSubmitTitle"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="delivery-return-submit-header">
              <div>
                <h2
                  className="delivery-return-submit-title"
                  id="deliveryReturnSubmitTitle"
                >
                  Submit Return Report
                </h2>
                <div className="delivery-return-submit-subtitle">
                  Report a failed or returned delivery.
                </div>
              </div>
              <button
                type="button"
                className="delivery-return-submit-close"
                aria-label="Close submit return"
                onClick={closeReturnSubmit}
              />
            </div>

            <div className="delivery-return-submit-form">
              <label className="delivery-return-submit-field">
                <span>
                  Order #<strong>*</strong>
                </span>
                <input placeholder="e.g. ORd-2451" />
              </label>
              <label className="delivery-return-submit-field">
                <span>
                  Customer Name<strong>*</strong>
                </span>
                <input placeholder="Full name" />
              </label>
              <div className="delivery-return-submit-field">
                <span>
                  Return Reason<strong>*</strong>
                </span>
                <CustomDropdown
                  className="delivery-return-reason-select"
                  openClassName="delivery-return-reason-select-open"
                  menuClassName="delivery-return-reason-select-menu"
                  optionClassName="delivery-return-reason-select-option"
                  value={returnReason}
                  onChange={setReturnReason}
                  options={returnReasonOptions}
                  placeholder="Select a reason..."
                  rightIcon={
                    <ArrowDownIcon className="delivery-return-reason-chevron" />
                  }
                />
              </div>
              <label className="delivery-return-submit-field">
                <span>Amount of Money</span>
                <input placeholder="$0.00" />
              </label>
              <label className="delivery-return-submit-field delivery-return-submit-field-wide">
                <span>Driver Notes</span>
                <textarea placeholder="Add any relevant notes about the return..." />
              </label>
              <label className="delivery-return-submit-field delivery-return-submit-field-wide">
                <span>Driver Name</span>
                <input placeholder="Assigned driver (optional)" />
              </label>
            </div>

            <div className="delivery-return-submit-actions">
              <button
                type="button"
                className="delivery-return-submit-cancel"
                onClick={closeReturnSubmit}
              >
                Cancel
              </button>
              <button
                type="button"
                className="delivery-return-submit-primary"
                onClick={submitReturnReport}
              >
                Submit Invoice
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {invoiceOrder ? (
        <div className="delivery-invoice-overlay" onMouseDown={closeInvoice}>
          <div
            className="delivery-invoice-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="deliveryInvoiceTitle"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="delivery-invoice-header">
              <div className="delivery-invoice-title-group">
                <h2
                  className="delivery-invoice-title"
                  id="deliveryInvoiceTitle"
                >
                  INV-1001
                </h2>
                <span className="delivery-invoice-status">Active</span>
              </div>
              <button
                type="button"
                className="delivery-invoice-close"
                aria-label="Close invoice"
                onClick={closeInvoice}
              />
            </div>
            <div className="delivery-invoice-issued">Issued Aug 01, 2026</div>
            <div className="delivery-invoice-card">
              <div className="delivery-invoice-meta">
                <div>
                  <div className="delivery-invoice-label">Order</div>
                  <div className="delivery-invoice-label">Customer</div>
                  <div className="delivery-invoice-label">Delivery Company</div>
                </div>
                <div>
                  <div className="delivery-invoice-value delivery-invoice-link">
                    {invoiceOrder.id}
                  </div>
                  <div className="delivery-invoice-value">
                    {invoiceOrder.customer}
                  </div>
                  <div className="delivery-invoice-value">DHL</div>
                </div>
              </div>
              <div className="delivery-invoice-breakdown">
                <div className="delivery-invoice-breakdown-title">
                  Fee Breakdown
                </div>
                <div className="delivery-invoice-fee-row">
                  <span>Delivery Fee</span>
                  <span>$12.00</span>
                </div>
                <div className="delivery-invoice-fee-row">
                  <span>Cash on Delivery</span>
                  <span>-</span>
                </div>
                <div className="delivery-invoice-fee-row">
                  <span>Insurance Fee</span>
                  <span>$2.50</span>
                </div>
                <div className="delivery-invoice-fee-row delivery-invoice-total-row">
                  <span>Total</span>
                  <span>$14.50</span>
                </div>
              </div>
            </div>
            <div className="delivery-invoice-actions">
              <button
                type="button"
                className="delivery-invoice-cancel"
                onClick={closeInvoice}
              >
                Close
              </button>
              <button
                type="button"
                className="delivery-invoice-download"
                onClick={downloadInvoice}
              >
                Download PDF
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {isTransferPopupOpen ? (
        <div
          className="delivery-transfer-overlay"
          onMouseDown={closeTransferPopup}
        >
          <div
            className="delivery-transfer-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="deliveryTransferTitle"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="delivery-transfer-header">
              <span className="delivery-transfer-header-icon">
                <DeleveryManagementIcon stroke="#4072C8" strokeWidth="1.5" />
              </span>
              <div className="delivery-transfer-title-group">
                <h2
                  className="delivery-transfer-title"
                  id="deliveryTransferTitle"
                >
                  Select who will deliver your product
                </h2>
                <div className="delivery-transfer-subtitle">
                  Choose a delivery company or a driver to handle this delivery.
                </div>
              </div>
              <button
                type="button"
                className="delivery-transfer-close"
                aria-label="Close delivery transfer"
                onClick={closeTransferPopup}
              />
            </div>

            <div className="delivery-transfer-search">
              <SearchIcon fill="#667085" />
              <input
                value={transferSearch}
                onChange={(event) => setTransferSearch(event.target.value)}
                placeholder="Search company or driver..."
              />
            </div>

            <div className="delivery-transfer-tabs">
              <button
                type="button"
                className={`delivery-transfer-tab ${
                  transferTab === "companies"
                    ? "delivery-transfer-tab-active"
                    : ""
                }`}
                onClick={() => selectTransferTab("companies")}
              >
                <BuildingIcon />
                <span>Companies</span>
              </button>
              <button
                type="button"
                className={`delivery-transfer-tab ${
                  transferTab === "drivers"
                    ? "delivery-transfer-tab-active"
                    : ""
                }`}
                onClick={() => selectTransferTab("drivers")}
              >
                <IndividualIcon />
                <span>Solo Drivers</span>
              </button>
            </div>

            <div className="delivery-transfer-list-title">
              Delivery Companies
            </div>

            <div className="delivery-transfer-options">
              {filteredTransferOptions.map((option) => (
                <button
                  type="button"
                  className={`delivery-transfer-option ${
                    transferTab === "drivers"
                      ? "delivery-transfer-driver-option"
                      : ""
                  } ${
                    selectedTransferOption === option.id
                      ? "delivery-transfer-option-active"
                      : ""
                  }`}
                  key={option.id}
                  onClick={() => setSelectedTransferOption(option.id)}
                >
                  <span className="delivery-transfer-radio"></span>
                  {transferTab === "drivers" ? (
                    <>
                      <span className="delivery-transfer-avatar delivery-transfer-driver-avatar">
                        <PicIcon />
                      </span>
                      <span className="delivery-transfer-driver-main">
                        <span className="delivery-transfer-option-name">
                          {option.name}
                        </span>
                        <span className="delivery-transfer-driver-rating">
                          <span
                            className="delivery-transfer-star"
                            aria-hidden="true"
                          ></span>{" "}
                          {option.rating} ({option.reviews})
                        </span>
                        <span className="delivery-transfer-option-description">
                          {option.description}
                        </span>
                      </span>
                      <span className="delivery-transfer-driver-vehicle">
                        <ShippingBusIcon />
                        <span>
                          <span className="delivery-transfer-driver-vehicle-name">
                            {option.vehicle}
                          </span>
                          <span className="delivery-transfer-driver-plate">
                            {option.plate}
                          </span>
                        </span>
                      </span>
                      <span className="delivery-transfer-driver-status">
                        <span className="delivery-transfer-available">
                          <span className="delivery-transfer-available-dot"></span>
                          Available
                        </span>
                        <span>ETA: {option.eta}</span>
                        <span>{option.distance}</span>
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="delivery-transfer-avatar">
                        {option.initials}
                      </span>
                      <span className="delivery-transfer-option-main">
                        <span className="delivery-transfer-option-name">
                          {option.name}
                        </span>
                        <span className="delivery-transfer-option-description">
                          {option.description}
                        </span>
                        <span className="delivery-transfer-verified">
                          <VerifiedSheild />
                          Verified Company
                        </span>
                      </span>
                      <span className="delivery-transfer-option-meta">
                        <span>
                          <span
                            className="delivery-transfer-star"
                            aria-hidden="true"
                          ></span>{" "}
                          {option.rating} ({option.reviews})
                        </span>
                        <span>ETA: {option.eta}</span>
                      </span>
                    </>
                  )}
                </button>
              ))}
            </div>

            <div className="delivery-transfer-actions">
              <button
                type="button"
                className="delivery-transfer-cancel"
                onClick={closeTransferPopup}
              >
                Cancel
              </button>
              <button
                type="button"
                className="delivery-transfer-confirm"
                onClick={confirmTransferSelection}
              >
                {transferTab === "drivers"
                  ? "Confirm Driver"
                  : "Confirm Selection"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {isNewDeliveryOpen ? (
        <div className="delivery-new-overlay" onMouseDown={closeNewDelivery}>
          <div
            className="delivery-new-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="deliveryNewTitle"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="delivery-new-header">
              <div>
                <h2 className="delivery-new-title" id="deliveryNewTitle">
                  New Delivery
                </h2>
                <div className="delivery-new-subtitle">
                  Create a delivery request
                </div>
              </div>
              <button
                type="button"
                className="delivery-new-close"
                aria-label="Close new delivery"
                onClick={closeNewDelivery}
              />
            </div>

            <div className="delivery-new-form">
              <label className="delivery-new-field">
                <span>
                  Customer Name<strong>*</strong>
                </span>
                <input placeholder="Full name" />
              </label>
              <label className="delivery-new-field">
                <span>
                  Phone<strong>*</strong>
                </span>
                <input placeholder="+96170 000 000" />
              </label>
              <label className="delivery-new-field">
                <span>
                  City<strong>*</strong>
                </span>
                <input placeholder="e.g. Beirut" />
              </label>
              <label className="delivery-new-field">
                <span>
                  Address<strong>*</strong>
                </span>
                <input placeholder="e.g. Baabda" />
              </label>
              <label className="delivery-new-field delivery-new-field-wide">
                <span>
                  Products<strong>*</strong>
                </span>
                <input placeholder="e.g. Wireless Earbuds, Charging Dock" />
              </label>
              <label className="delivery-new-field">
                <span>
                  Packages<strong>*</strong>
                </span>
                <input placeholder="1" />
              </label>
              <label className="delivery-new-field">
                <span>
                  Amount ($)<strong>*</strong>
                </span>
                <input placeholder="0.00" />
              </label>
              <div className="delivery-new-field">
                <span>
                  Delivery Method<strong>*</strong>
                </span>
                <CustomDropdown
                  className="delivery-new-select"
                  openClassName="delivery-new-select-open"
                  menuClassName="delivery-new-select-menu"
                  optionClassName="delivery-new-select-option"
                  value={newDeliveryMethod}
                  onChange={setNewDeliveryMethod}
                  options={newDeliveryMethodOptions}
                  placeholder="Third-Party"
                  rightIcon={
                    <span className="delivery-table-select-chevron"></span>
                  }
                />
              </div>
              <div className="delivery-new-field">
                <span>
                  Delivery Company<strong>*</strong>
                </span>
                <CustomDropdown
                  className="delivery-new-select"
                  openClassName="delivery-new-select-open"
                  menuClassName="delivery-new-select-menu"
                  optionClassName="delivery-new-select-option"
                  value={newDeliveryCompany}
                  onChange={setNewDeliveryCompany}
                  options={newDeliveryCompanyOptions}
                  placeholder="DHL"
                  rightIcon={
                    <span className="delivery-table-select-chevron"></span>
                  }
                />
              </div>
              <label className="delivery-new-field delivery-new-field-wide">
                <span>Driver name</span>
                <input placeholder="Driver name (optional)" />
              </label>
            </div>

            <div className="delivery-new-actions">
              <button
                type="button"
                className="delivery-new-cancel"
                onClick={closeNewDelivery}
              >
                Cancel
              </button>
              <button
                type="button"
                className="delivery-new-submit"
                onClick={createDelivery}
              >
                Create Delivery
              </button>
            </div>
          </div>
        </div>
      ) : null}
      <div className="business-hub-header-container">
        <BusinessHubHeader />
      </div>

      <div className="business-hub-page-content-container">
        <div className="business-hub-navigation-sidebar">
          <BusinessHubNavigation mode="prod" active="N" onSelect={() => {}} />
        </div>

        <div className="business-hub-content-container">
          <div className="content-container">
            <div className="payment-main-container delivery-management-page-shell">
              <div className="payment-header">
                <div className="payment-header-wrapper">
                  <div className="payment-header-icon">
                    <DeleveryManagementIcon strokeWidth="2.5px" />
                  </div>

                  <div className="payment-title-container">
                    <div className="payment-title">Delivery Management</div>
                    <div className="payment-header-text">
                      Azalea logistics workspace - August 3, 2026
                    </div>
                  </div>
                </div>
                <div className="delivery-management-header-button">
                  <div className="wallet-filter-wrap">
                    <button
                      type="button"
                      className="wallet-filter-button"
                      onClick={openFilter}
                    >
                      <FilterIcon />
                      <span>Filter</span>
                    </button>
                  </div>
                  <div className="wallet-filter-wrap">
                    <button
                      type="button"
                      className="wallet-filter-button"
                      onClick={openExport}
                    >
                      <span>Export CSV</span>
                    </button>
                  </div>
                  {/* <div className="wallet-filter-wrap">
                    <button
                      type="button"
                      className="wallet-filter-button-new"
                      onClick={openNewDelivery}
                    >
                      <PlusIcon /> <span>New Delivery</span>
                    </button>
                  </div> */}
                </div>
              </div>
              <div className="delivery-summary-row">
                {[
                  {
                    label: "Total Orders",
                    helper: "/ dashboard",
                    value: displayedDeliveryStats.totalOrders,
                  },
                  {
                    label: "Pending",
                    helper: "/ awaiting",
                    value: displayedDeliveryStats.pendingCount,
                  },
                  {
                    label: "Confirmed",
                    helper: "/ active",
                    value: displayedDeliveryStats.confirmedCount,
                  },
                  {
                    label: "Out for Delivery",
                    helper: "/ en route",
                    value: displayedDeliveryStats.outForDeliveryCount,
                  },
                ].map((item) => (
                  <div className="delivery-summary-card" key={item.label}>
                    <div className="delivery-summary-number">{item.value}</div>
                    <div className="delivery-summary-label">
                      {item.label} <span>{item.helper}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="delivery-table-tabs">
                <button
                  type="button"
                  className={`delivery-table-tab ${
                    activeDeliveryTab === "orders"
                      ? "delivery-table-tab-active"
                      : ""
                  }`}
                  onClick={() => selectDeliveryTab("orders")}
                >
                  Orders
                </button>
                <button
                  type="button"
                  className={`delivery-table-tab ${
                    activeDeliveryTab === "returns"
                      ? "delivery-table-tab-active"
                      : ""
                  }`}
                  onClick={() => selectDeliveryTab("returns")}
                >
                  Returns
                </button>
                <button
                  type="button"
                  className={`delivery-table-tab ${
                    activeDeliveryTab === "invoices"
                      ? "delivery-table-tab-active"
                      : ""
                  }`}
                  onClick={() => selectDeliveryTab("invoices")}
                >
                  Invoices
                </button>
              </div>

              {activeDeliveryTab === "returns" ? (
                <div
                  key="delivery-returns-table"
                  className="delivery-management-table-block delivery-returns-management-block"
                >
                  <div className="delivery-returns-panel">
                    <div className="delivery-returns-header">
                      <div>
                        <div className="delivery-returns-title">
                          Return Management
                        </div>
                        <div className="delivery-returns-subtitle">
                          Failed deliveries and return reports.
                        </div>
                      </div>
                      <button
                        type="button"
                        className="delivery-returns-submit-button"
                        onClick={openReturnSubmit}
                      >
                        + Submit Return
                      </button>
                    </div>
                    <div className="delivery-returns-table-wrap">
                      <div className="delivery-returns-table">
                        <div className="delivery-returns-row delivery-returns-head-row">
                          <div className="delivery-secondary-checkbox-cell">
                            <input
                              type="checkbox"
                              checked={allReturnRowsChecked}
                              onChange={toggleAllReturns}
                            />
                          </div>
                          <div>Return ID</div>
                          <div>Order #</div>
                          <div>Customer</div>
                          <div>Return Reason</div>
                          <div>Date</div>
                          <div>Driver</div>
                          <div>Status</div>
                          <div>Refund</div>
                        </div>
                        {returnOrders.map((returnOrder) => (
                          <div
                            className="delivery-returns-row"
                            key={returnOrder.id}
                          >
                            <div
                              className="delivery-secondary-checkbox-cell"
                              data-label="Select"
                            >
                              <input
                                type="checkbox"
                                checked={selectedReturnIds.includes(
                                  returnOrder.id,
                                )}
                                onChange={() => toggleReturnRow(returnOrder.id)}
                              />
                            </div>
                            <div
                              className="delivery-returns-link"
                              data-label="Return ID"
                            >
                              {returnOrder.id}
                            </div>
                            <div data-label="Order #">
                              {returnOrder.orderId}
                            </div>
                            <div data-label="Customer">
                              {returnOrder.customer}
                            </div>
                            <div data-label="Return Reason">
                              {returnOrder.reason}
                            </div>
                            <div data-label="Date">{returnOrder.date}</div>
                            <div data-label="Driver">{returnOrder.driver}</div>
                            <div data-label="Status">
                              <span
                                className={`delivery-returns-status delivery-returns-status-${returnOrder.statusValue}`}
                              >
                                {returnOrder.status}
                              </span>
                            </div>
                            <div
                              className="delivery-returns-refund"
                              data-label="Refund"
                            >
                              {returnOrder.refund}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ) : activeDeliveryTab === "invoices" ? (
                <div
                  key="delivery-invoices-table"
                  className="delivery-management-table-block delivery-invoices-management-block"
                >
                  <div className="delivery-invoices-panel">
                    <div className="delivery-invoices-header">
                      <div>
                        <div className="delivery-invoices-title">
                          Invoice Management
                        </div>
                        <div className="delivery-invoices-subtitle">
                          Auto-generated delivery invoices per partner company.
                        </div>
                      </div>
                      <div className="delivery-invoices-header-actions">
                        {/* <button
                          type="button"
                          className="delivery-invoices-export-button"
                        >
                          Export
                        </button> */}
                        <button
                          type="button"
                          className="delivery-invoices-new-button"
                        >
                          + New Invoice
                        </button>
                      </div>
                    </div>
                    <div className="delivery-invoices-table-wrap">
                      <div className="delivery-invoices-table">
                        <div className="delivery-invoices-row delivery-invoices-head-row">
                          <div className="delivery-secondary-checkbox-cell">
                            <input
                              type="checkbox"
                              checked={allInvoiceRowsChecked}
                              onChange={toggleAllInvoices}
                            />
                          </div>
                          <div>Invoice #</div>
                          <div>Order #</div>
                          <div>Delivery Company</div>
                          <div>Customer</div>
                          <div>Delivery Fee</div>
                          <div>COD Amount</div>
                          <div>Insurance</div>
                          <div>Total</div>
                          <div>Status</div>
                          <div>Date</div>
                          <div></div>
                        </div>
                        {deliveryInvoices.map((invoice) => (
                          <div
                            className="delivery-invoices-row"
                            key={invoice.id}
                          >
                            <div
                              className="delivery-secondary-checkbox-cell"
                              data-label="Select"
                            >
                              <input
                                type="checkbox"
                                checked={selectedInvoiceIds.includes(
                                  invoice.id,
                                )}
                                onChange={() => toggleInvoiceRow(invoice.id)}
                              />
                            </div>
                            <div
                              className="delivery-invoices-link"
                              data-label="Invoice #"
                            >
                              {invoice.id}
                            </div>
                            <div data-label="Order #">{invoice.orderId}</div>
                            <div data-label="Delivery Company">
                              {invoice.company}
                            </div>
                            <div data-label="Customer">{invoice.customer}</div>
                            <div data-label="Delivery Fee">
                              {invoice.deliveryFee}
                            </div>
                            <div data-label="COD Amount">
                              {invoice.codAmount}
                            </div>
                            <div data-label="Insurance">
                              {invoice.insurance}
                            </div>
                            <div
                              className="delivery-invoices-total"
                              data-label="Total"
                            >
                              {invoice.total}
                            </div>
                            <div data-label="Status">
                              <span
                                className={`delivery-invoices-status delivery-invoices-status-${invoice.statusValue}`}
                              >
                                {invoice.status}
                              </span>
                            </div>
                            <div data-label="Date">{invoice.date}</div>
                            <div data-label="Action">
                              <button
                                type="button"
                                className="delivery-invoices-view-button"
                                onClick={() =>
                                  openInvoice({
                                    id: invoice.orderId,
                                    customer: invoice.customer,
                                  })
                                }
                              >
                                View
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  key="delivery-orders-table"
                  className="delivery-management-table-block"
                >
                  <div className="delivery-management-table-toolbar">
                    <div className="delivery-management-search-bar">
                      <span className="delivery-management-search-icon">
                        <SearchIcon fill="#98A2B3" />
                      </span>
                      <input
                        className="delivery-management-search-input"
                        placeholder="Search by order #, customer, or product..."
                        value={searchValue}
                        onChange={(event) => setSearchValue(event.target.value)}
                      />
                    </div>
                    <div className="delivery-management-table-filters">
                      <CustomDropdown
                        className="delivery-table-filter-select"
                        openClassName="delivery-table-filter-select-open"
                        menuClassName="delivery-table-filter-select-menu"
                        optionClassName="delivery-table-filter-select-option"
                        value={statusFilter}
                        onChange={setStatusFilter}
                        options={deliveryStatusOptions}
                        placeholder="All Statuses"
                        rightIcon={
                          <span className="delivery-table-select-chevron"></span>
                        }
                      />
                      <button
                        type="button"
                        className={`delivery-table-filter-select delivery-table-filter-select-trigger ${
                          isTransferPopupOpen
                            ? "delivery-table-filter-select-open"
                            : ""
                        }`}
                        onClick={openTransferPopup}
                      >
                        <span className="filter-popup-container-rows-input-data">
                          {
                            deliveryTypeOptions.find(
                              (option) => option.value === deliveryTypeFilter,
                            )?.label
                          }
                        </span>
                        <span className="delivery-table-select-chevron"></span>
                      </button>
                    </div>
                  </div>

                  <div className="delivery-info-display-table">
                    <div className="delivery-info-table">
                      <div className="delivery-table-header-row">
                        <div className="delivery-cell delivery-cell-order">
                          <div className="delivery-cell-header-data">
                            <div className="delivery-checkbox-cell">
                              <input
                                type="checkbox"
                                checked={allDeliveryRowsChecked}
                                onChange={toggleAllDeliveries}
                              />
                            </div>
                            Orders #
                          </div>
                        </div>
                        <div className="delivery-cell delivery-cell-customer">
                          Customer
                        </div>
                        <div className="delivery-cell delivery-cell-delivered">
                          Delivered By
                        </div>
                        <div className="delivery-cell delivery-cell-products">
                          Products
                        </div>
                        <div className="delivery-cell delivery-cell-packages">
                          PKG
                        </div>
                        <div className="delivery-cell delivery-cell-status">
                          Delivery Status
                        </div>
                        <div className="delivery-cell delivery-cell-payment">
                          Payment
                        </div>
                        <div className="delivery-cell delivery-cell-amount">
                          Amount
                        </div>
                        <div className="delivery-cell delivery-cell-dimensions">
                          Pkg Dimensions(CM)
                        </div>
                        <div className="delivery-cell delivery-cell-actions">
                          Actions
                        </div>
                      </div>

                      {isDeliveryTableLoading ? (
                        <div className="delivery-table-state-row">
                          Loading delivery orders...
                        </div>
                      ) : null}
                      {shouldShowDeliveryTableError ? (
                        <div className="delivery-table-state-row delivery-table-error-row">
                          <span>{deliveryApiError}</span>
                          <button
                            type="button"
                            className="delivery-table-retry-button"
                            onClick={() =>
                              loadDeliveryOrders({
                                nextTab: "orders",
                                nextPage: deliveryApiPagination.page,
                                nextSelectedOrderId: null,
                                nextDateRange: appliedRange,
                              })
                            }
                          >
                            Retry
                          </button>
                        </div>
                      ) : null}
                      {shouldShowDeliveryEmptyState ? (
                        <div className="delivery-table-state-row">
                          No delivery orders found.
                        </div>
                      ) : null}

                      {!isDeliveryTableLoading &&
                        !shouldShowDeliveryTableError &&
                        currentDeliveryOrders.map((order) => (
                        <React.Fragment key={order.id}>
                          <div
                            className={`delivery-table-row ${
                              expandedOrderId === order.id
                                ? "delivery-table-row-selected"
                                : ""
                            }`}
                            onClick={() => toggleDeliveryDetails(order)}
                          >
                            <div
                              className="delivery-cell delivery-cell-order"
                              data-label="Order"
                            >
                              <div
                                className="delivery-checkbox-cell"
                                onClick={(event) => event.stopPropagation()}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedDeliveryIds.includes(
                                    order.id,
                                  )}
                                  onChange={() => toggleDeliveryRow(order.id)}
                                />
                              </div>
                              <span className="delivery-order-id">
                                {order.id}
                              </span>
                            </div>
                            <div
                              className="delivery-cell delivery-cell-customer"
                              data-label="Customer"
                            >
                              <div className="delivery-customer-name">
                                {order.customer}
                              </div>
                              <div className="delivery-customer-location">
                                {order.location}
                              </div>
                            </div>
                            <div
                              className="delivery-cell delivery-cell-delivered"
                              data-label="Delivered By"
                            >
                              <span
                                className={`delivery-provider-pill delivery-provider-${getStatusClassName(
                                  order.deliveredBy,
                                )}`}
                              >
                                {order.deliveredBy}
                              </span>
                            </div>
                            <div
                              className="delivery-cell delivery-cell-products"
                              data-label="Products"
                            >
                              {order.products}
                            </div>
                            <div
                              className="delivery-cell delivery-cell-packages"
                              data-label="PKG"
                            >
                              {order.packages}
                            </div>
                            <div
                              className="delivery-cell delivery-cell-status"
                              data-label="Delivery Status"
                            >
                              <span
                                className={`delivery-status-pill delivery-status-${order.statusValue}`}
                              >
                                <span className="delivery-status-dot"></span>
                                {order.deliveryStatus}
                              </span>
                            </div>
                            <div
                              className="delivery-cell delivery-cell-payment"
                              data-label="Payment"
                            >
                              <span
                                className={`delivery-payment-pill delivery-payment-${getStatusClassName(
                                  order.payment,
                                )}`}
                              >
                                {order.payment}
                              </span>
                            </div>
                            <div
                              className="delivery-cell delivery-cell-amount"
                              data-label="Amount"
                            >
                              {order.amount}
                            </div>
                            <div
                              className="delivery-cell delivery-cell-dimensions"
                              data-label="Pkg Dimensions"
                            >
                              {order.dimensions}
                            </div>
                            <div
                              className="delivery-cell delivery-cell-actions"
                              data-label="Actions"
                            >
                              <button
                                type="button"
                                className={`delivery-row-action-button ${
                                  expandedOrderId === order.id
                                    ? "delivery-row-action-button-open"
                                    : ""
                                }`}
                                aria-label={`Toggle details for ${order.id}`}
                                aria-expanded={expandedOrderId === order.id}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  toggleDeliveryDetails(order);
                                }}
                              >
                                <ArrowDownIcon className="delivery-row-action-icon" />
                              </button>
                            </div>
                          </div>
                          {expandedOrderId === order.id ? (
                            <div className="delivery-details-row">
                              {hasApiDeliveryRows && isLoadingDeliveryOrders ? (
                                <div className="delivery-details-line">
                                  Loading order details...
                                </div>
                              ) : null}
                              {hasApiDeliveryRows && deliveryApiError ? (
                                <div className="delivery-details-line">
                                  {deliveryApiError}
                                </div>
                              ) : null}
                              <div className="delivery-details-summary">
                                <div className="delivery-details-column">
                                  <div className="delivery-details-title">
                                    Customer Info
                                  </div>
                                  <div className="delivery-details-line">
                                    Phone:{" "}
                                    {getFirstValue(
                                      getExpandedApiDetails(order)?.customerPhone,
                                      getExpandedApiDetails(order)?.phoneNumber,
                                      getExpandedApiDetails(order)?.customer
                                        ?.phone,
                                      "+961 70 123 456",
                                    )}
                                  </div>
                                  <div className="delivery-details-line">
                                    City:{" "}
                                    {getFirstValue(
                                      getExpandedApiDetails(order)
                                        ?.deliveryLocation,
                                      getExpandedApiDetails(order)
                                        ?.deliveryAddress,
                                      order.location,
                                    )}
                                  </div>
                                  <div className="delivery-details-line">
                                    Method:{" "}
                                    {getFirstValue(
                                      getExpandedApiDetails(order)
                                        ?.assignedDriver?.name,
                                      order.deliveredBy,
                                    )}
                                  </div>
                                </div>
                                <div className="delivery-details-column">
                                  <div className="delivery-details-title">
                                    Order Details
                                  </div>
                                  <div className="delivery-details-line">
                                    Products:{" "}
                                    {getExpandedProducts(
                                      getExpandedApiDetails(order),
                                      order,
                                    )}
                                  </div>
                                  <div className="delivery-details-line">
                                    Packages:{" "}
                                    {getExpandedPackages(
                                      getExpandedApiDetails(order),
                                      order,
                                    )}
                                  </div>
                                  <div className="delivery-details-line">
                                    Order Date:{" "}
                                    {getFirstValue(
                                      getExpandedApiDetails(order)?.placedAtLabel,
                                      getExpandedApiDetails(order)?.createdAt,
                                      getExpandedApiDetails(order)?.orderDate,
                                      "Aug 01, 2026",
                                    )}
                                  </div>
                                </div>
                                <div className="delivery-details-column delivery-details-actions">
                                  <div className="delivery-details-title">
                                    Quick Actions
                                  </div>
                                  <div className="delivery-details-action-list">
                                    {hasApiDeliveryRows &&
                                    getAvailableNextStatuses(
                                      getExpandedApiDetails(order),
                                    ).length > 0 ? (
                                      <CustomDropdown
                                        className="delivery-details-status-select"
                                        menuClassName="delivery-details-status-select-menu"
                                        optionClassName="delivery-details-status-select-option"
                                        value={selectedDeliveryNextStatus}
                                        onChange={setSelectedDeliveryNextStatus}
                                        options={getAvailableNextStatuses(
                                          getExpandedApiDetails(order),
                                        ).map((status) => ({
                                          label: getStatusLabel(status),
                                          value: status,
                                        }))}
                                        rightIcon={
                                          <span className="delivery-table-select-chevron"></span>
                                        }
                                      />
                                    ) : null}
                                    <button
                                      type="button"
                                      className="delivery-details-primary-action"
                                      onClick={() =>
                                        handleDeliveryStatusUpdate(order)
                                      }
                                      disabled={
                                        hasApiDeliveryRows &&
                                        (!selectedDeliveryNextStatus ||
                                          isSubmittingDeliveryAction)
                                      }
                                    >
                                      {isSubmittingDeliveryAction
                                        ? "Saving..."
                                        : "Update Status"}
                                    </button>
                                    {canRespondToPendingDeliveryOrder(
                                      getExpandedApiDetails(order),
                                      order,
                                    ) ? (
                                      <>
                                        <button
                                          type="button"
                                          className="delivery-details-accept-action"
                                          onClick={() =>
                                            handleDeliveryOrderAction(
                                              "accept",
                                              order,
                                            )
                                          }
                                          disabled={isSubmittingDeliveryAction}
                                        >
                                          {isSubmittingDeliveryAction
                                            ? "Saving..."
                                            : "Accept Order"}
                                        </button>
                                        <button
                                          type="button"
                                          className="delivery-details-reject-action"
                                          onClick={() =>
                                            handleDeliveryOrderAction(
                                              "reject",
                                              order,
                                            )
                                          }
                                          disabled={isSubmittingDeliveryAction}
                                        >
                                          Reject
                                        </button>
                                      </>
                                    ) : null}
                                    <button
                                      type="button"
                                      className="delivery-details-secondary-action"
                                      onClick={() => openAssignDriver(order)}
                                    >
                                      Assign Driver
                                    </button>
                                    <button
                                      type="button"
                                      className="delivery-details-secondary-action"
                                      onClick={() => openInvoice(order)}
                                    >
                                      View Invoice
                                    </button>
                                  </div>
                                  {deliveryActionError ? (
                                    <div className="delivery-details-action-error">
                                      {deliveryActionError}
                                    </div>
                                  ) : null}
                                </div>
                              </div>
                              <div className="delivery-package-section">
                                <div className="delivery-details-title">
                                  Package Dimensions
                                </div>
                                <div className="delivery-package-table">
                                  <div className="delivery-package-table-row delivery-package-table-header">
                                    <div>#</div>
                                    <div>Size</div>
                                    <div>Type</div>
                                    <div>L x W x H (CM)</div>
                                    <div>Weight (KG)</div>
                                    <div>Volume (CM3)</div>
                                  </div>
                                  <div className="delivery-package-table-row">
                                    <div>1</div>
                                    <div>S</div>
                                    <div>Box</div>
                                    <div>{order.dimensions}</div>
                                    <div>0.4</div>
                                    <div>1,200</div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ) : null}
                        </React.Fragment>
                        ))}
                    </div>
                  </div>

                  <div className="delivery-table-footer">
                    <div className="delivery-table-results">
                      Showing {currentDeliveryOrders.length} of{" "}
                      {filteredDeliveryOrders.length} orders
                    </div>
                    <div className="delivery-nav-table-bar">
                      <button
                        type="button"
                        className="delivery-nav-bar-button"
                        disabled={
                          displayDeliveryPage === 1 ||
                          isDeliveryTableLoading ||
                          shouldShowDeliveryTableError
                        }
                        onClick={() => handleDeliveryPageChange(-1)}
                      >
                        <ArrowBackIcon />
                        <span>Pre</span>
                      </button>
                      <button
                        type="button"
                        className="delivery-nav-page-number delivery-active-page"
                      >
                        {displayDeliveryPage}
                      </button>
                      <button
                        type="button"
                        className="delivery-nav-bar-button"
                        disabled={
                          displayDeliveryPage === totalDeliveryPages ||
                          isDeliveryTableLoading ||
                          shouldShowDeliveryTableError
                        }
                        onClick={() => handleDeliveryPageChange(1)}
                      >
                        <ArrowFront />
                        <span>Next</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DeleveryManagment;
